/**
 * RadioStatus — "FFR Radio ▶" widget at the top of the user-list panel.
 *
 * Playback now uses FFRRadio (browser-side MP3 extraction from random
 * FFR level SWFs); the legacy Icecast `nowPlaying` field is no longer
 * used. Off by default — the user must hit ▶ to start the radio. When
 * the current track ends, the radio picks another random level.
 *
 * The 🔈/🔇 button is the global "play sounds" mute (same pref as
 * before) — it silences the radio AND chat sound effects together.
 */
import { useEffect, useRef, useState } from 'react';
import { usePrefs } from '../hooks/usePrefs.js';
import { FFRRadio } from '../lib/ffrRadio.js';

interface Props {
    /** Legacy Icecast nowPlaying — unused for playback now but kept for type compat. */
    nowPlaying?: { title: string; artist: string; url?: string } | null;
}

export function RadioStatus(_props: Props) {
    const [prefs, setPref] = usePrefs();
    const [playing, setPlaying] = useState(false);
    const [trackTitle, setTrackTitle] = useState('');
    const radioRef = useRef<FFRRadio | null>(null);

    useEffect(() => {
        // Lazily instantiate one radio per RadioStatus instance.
        radioRef.current = new FFRRadio({
            onTitleChange: setTrackTitle,
            onError: (e) => { setTrackTitle(`error: ${e}`); setPlaying(false); },
        });
        return () => {
            radioRef.current?.stop();
            radioRef.current = null;
        };
    }, []);

    // Reflect mute pref → radio audio
    useEffect(() => {
        radioRef.current?.setMuted(!prefs.playSounds);
    }, [prefs.playSounds]);

    function toggle() {
        const r = radioRef.current;
        if (!r) return;
        if (playing) {
            r.stop();
            setPlaying(false);
        } else {
            void r.start();
            setPlaying(true);
        }
    }
    function skip() {
        if (playing) void radioRef.current?.next();
    }

    return (
        <div className="ul-radio" title={trackTitle ? `Now playing: ${trackTitle}` : 'FFR Radio — click ▶ to play random levels'}>
            <span className="ul-radio-label">FFR Radio</span>
            <button className="ul-mute-btn"
                    onClick={() => setPref('playSounds', !prefs.playSounds)}
                    title={prefs.playSounds ? 'Mute all sounds' : 'Unmute sounds'}
                    aria-label={prefs.playSounds ? 'mute' : 'unmute'}>
                {prefs.playSounds ? '🔈' : '🔇'}
            </button>
            <button className={`ul-radio-btn ${playing ? 'ul-radio-btn-playing' : ''}`}
                    onClick={toggle}
                    title={playing ? 'Stop radio' : 'Play radio (random FFR level)'}
                    aria-label={playing ? 'stop' : 'play'}>
                {playing ? '❚❚' : '▶'}
            </button>
            {playing && (
                <button className="ul-radio-btn ul-radio-skip-btn"
                        onClick={skip}
                        title="Skip to next track"
                        aria-label="skip">
                    »
                </button>
            )}
            {trackTitle && (
                <div className="ul-radio-track" title={trackTitle}>{trackTitle}</div>
            )}
        </div>
    );
}
